import { useRef, useState, useEffect } from "react";
import clsx from "clsx";

import { isNil } from "lodash";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/navigation";

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import ScaleLoader from "react-spinners/ScaleLoader";
import { X } from "react-bootstrap-icons";
import { initializeFancybox } from "@/helpers/initializeFancybox";

/**
 * SocialPropertyInfoImageSwiper component displays Property Images with swiper on Listing Page
 * @param {Object} props - Component props
 * @param {Object} props.property - Properties Details
 * @returns {JSX.Element} - Property swiper with statusIndicator Overlay.
 */
interface SocialPropertyInfoImageSwiperProps {
    property: any;
}

const SocialPropertyInfoImageSwiper = ({ property }: SocialPropertyInfoImageSwiperProps) => {
    const images = property?.imageUrls || [];
    const statusIndicator = property?.statusIndicator;
    const swiperRef = useRef<any>(null);
    const [loadedImages, setLoadedImages] = useState<boolean[]>(Array(images?.length || 0).fill(false));
    const [isVerticalModalOpen, setIsVerticalModalOpen] = useState(false);

    useEffect(() => {
        if (!isVerticalModalOpen) return;

        Fancybox.bind(`[data-fancybox="gallery-${property.id}"]`, {
            Toolbar: false,
            Thumbs: false,
        } as any);

        return () => {
            Fancybox.unbind(`[data-fancybox="gallery-${property.id}"]`);
        };
    }, [isVerticalModalOpen, property?.id]);

    useEffect(() => {
        if (swiperRef.current?.swiper) {
            swiperRef.current.swiper.slideTo(0);
        }
    }, [property]);

    useEffect(() => {
        const storedIndex = parseInt(localStorage.getItem(`swiper-index-${property.id}`) || "0", 10);
        if (!isNaN(storedIndex) && swiperRef.current?.swiper) {
            swiperRef.current.swiper.slideTo(storedIndex);
        }
    }, [property]);

    const handleImageLoad = (index: number) => {
        setLoadedImages((prev) => {
            const newLoaded = [...prev];
            newLoaded[index] = true;
            return newLoaded;
        });
    };

    return (
        <>
            <Swiper
                ref={swiperRef}
                modules={[Pagination, EffectFade, Navigation]}
                loop
                pagination={{
                    el: ".swiper-pagination",
                    type: "fraction",
                    renderFraction: function (currentClass: string, totalClass: string) {
                        if (images.length < 5) {
                            return `<div class="pagination-fraction-wrapper px-1 insufficient-image-text" data-cy="pagination">
                            <span><i>MLS rules require us to keep the rest of these photos top secret</i></span>
                        </div>`;
                        }

                        return `<div class="pagination-fraction-wrapper px-1" data-cy="pagination">
                                <span class="${currentClass}"></span> of <span class="${totalClass}"></span>
                            </div>`;
                    },
                }}
                navigation={{
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                }}
                data-cy="property-image-swiper"
                speed={400}
                effect="fade"
                className="property-image-swiper"
            >
                {images.map((image: any, index: number) => (
                    <SwiperSlide className="swiper-slide" key={index}>
                        <div
                            onClick={() => setIsVerticalModalOpen(true)}
                            className="image-container tw-relative tw-mx-0 tw-flex tw-justify-center tw-cursor-pointer"
                        >
                            <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-1/3 tw-z-50" />

                            {statusIndicator && (
                                <span
                                    className={clsx(
                                        "tw-absolute tw-top-[5%] tw-py-1 tw-px-4 tw-rounded-[500px] tw-z-[55] tw-right-[5%]",
                                        {
                                            "tw-bg-[#AAD880]": statusIndicator.toLowerCase() === "active",
                                            "tw-bg-[#e7ac2d]": statusIndicator.toLowerCase() === "contract",
                                            "tw-bg-[#494949]": statusIndicator.toLowerCase() === "closed",
                                        },
                                    )}
                                >
                                    {statusIndicator}
                                </span>
                            )}

                            {(!loadedImages[index] || isNil(loadedImages[index])) && (
                                <div className="tw-flex !tw-justify-center !tw-items-center tw-h-full">
                                    <ScaleLoader color="#63c19f" />
                                </div>
                            )}

                            <img
                                className="!tw-max-w-full !tw-h-auto !tw-object-contain md:!tw-h-full"
                                src={image}
                                alt={`Slide ${index}`}
                                onLoad={() => handleImageLoad(index)}
                                style={{ display: loadedImages[index] ? "block" : "none" }}
                            />
                        </div>
                    </SwiperSlide>
                ))}
                <div className="swiper-button-prev"></div>
                <div className="swiper-button-next"></div>
                <div className="swiper-pagination"></div>
            </Swiper>

            {/* Vertical Modal */}
            {isVerticalModalOpen && (
                <div
                    className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-black/90 tw-z-[9999] tw-overflow-y-auto"
                    onClick={() => setIsVerticalModalOpen(false)}
                >
                    <button
                        className="tw-fixed tw-top-[1.5svh] tw-right-6 tw-text-white tw-text-2xl tw-font-bold tw-z-[100] tw-bg-black/50 tw-rounded-full"
                        onClick={(e: any) => {
                            e.stopPropagation();
                            setIsVerticalModalOpen(false);
                        }}
                        aria-label="Close modal"
                    >
                        <X />
                    </button>

                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-[2px] tw-w-full md:tw-max-w-[600px] md:tw-mx-auto">
                        {images.map((image: any, index: number) => (
                            <a
                                key={index}
                                href={image}
                                className="tw-w-full"
                                style={{ cursor: "pointer" }}
                                onClick={(e: any) => {
                                    e.preventDefault();
                                    const options = {
                                        startIndex: index,
                                        Toolbar: { display: { right: ["close"] } },
                                        Thumbs: false,
                                        on: {
                                            close: () => {
                                                setIsVerticalModalOpen(true);
                                            },
                                        },
                                    };
                                    initializeFancybox(images, options);
                                }}
                            >
                                <img
                                    src={image}
                                    alt={`PropertySwiper ${index + 1}`}
                                    className="tw-w-full md:tw-max-h-screen tw-object-contain tw-rounded"
                                />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default SocialPropertyInfoImageSwiper;
